import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);       // null while loading
    const [salon, setSalon] = useState(null);
    const [ready, setReady] = useState(false);
    const [authed, setAuthed] = useState(false);

    const applySession = useCallback((data) => {
        setUser(data.user);
        setSalon(data.salon || null);
        setAuthed(true);
    }, []);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
            setSalon(data.salon || null);
            setAuthed(true);
            return data;
        } catch (e) {
            setAuthed(false);
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("crn_token");
        if (!token) { setReady(true); return; }
        refreshMe().finally(() => setReady(true));
    }, [refreshMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("crn_token", data.token);
        applySession(data);
        return data;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        localStorage.setItem("crn_token", data.token);
        applySession(data);
        return data;
    };

    const logout = () => {
        localStorage.removeItem("crn_token");
        setUser(null);
        setSalon(null);
        setAuthed(false);
    };

    return (
        <AuthContext.Provider value={{ user, salon, setSalon, ready, authed, login, register, logout, refreshMe, formatApiError }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
